import AWS from "aws-sdk";
import config from "../config";

class ObjectDetectionHandler {
  constructor(dataChannel, viewerId, onDetection) {
    this.dataChannel = dataChannel;
    this.viewerId = viewerId;
    this.onDetection = onDetection; // Callback for detection during room scan

    // Initialize Rekognition with credentials from config
    this.rekognition = new AWS.Rekognition({
      region: config.aws.region,
      credentials: {
        accessKeyId: config.aws.credentials.accessKeyId,
        secretAccessKey: config.aws.credentials.secretAccessKey,
      },
    });

    // List of prohibited items with actual Rekognition labels
    this.PROHIBITED_ITEMS = {
      Phone: ["Cell Phone", "Mobile Phone", "Phone"],
      Headphone: ["Headphone", "Headphones", "Headset"],
      Book: ["Book", "Text Book", "Notebook"],
    };

    // Cooldown tracking
    this.lastObjectDetectionTime = 0;
    this.OBJECT_DETECTION_COOLDOWN = 15000;
    this.lastFaceAlertTime = 0;
    this.FACE_ALERT_COOLDOWN = 2000;
    this.isRoomScanMode = false;

    console.log(
      "[ObjectDetectionHandler] Initialized with cooldown settings:",
      {
        objectCooldown: "15 seconds",
        faceCooldown: "2 seconds",
      }
    );
  }

  // Send alert to master
  sendAlert(type, details) {
    const now = Date.now();

    // Track in S3 with cooldown status
    if (
      type === "prohibited_objects" &&
      typeof window.trackViolation === "function"
    ) {
      const isInCooldown =
        now - this.lastObjectDetectionTime < this.OBJECT_DETECTION_COOLDOWN;
      window.trackViolation("object_detection", {
        type,
        details,
        timestamp: new Date().toISOString(),
        inCooldown: isInCooldown,
      });
      this.lastObjectDetectionTime = now;
    }

    // Check data channel before proceeding
    if (!this.dataChannel) {
      console.error("❌ [ObjectDetectionHandler] No data channel available");
      return;
    }

    if (this.dataChannel.readyState !== "open") {
      console.error(
        `❌ [ObjectDetectionHandler] Data channel not open (state: ${this.dataChannel.readyState})`
      );
      return;
    }

    // Format alert exactly as proctor expects
    const alert = {
      type: "object_alert",
      alertType: type,
      viewerId: this.viewerId,
      timestamp: new Date().toISOString(),
      details: details,
    };

    try {
      // Send through data channel immediately without cooldown
      const alertStr = JSON.stringify(alert);
      this.dataChannel.send(alertStr);

      // Log what was sent to proctor
      console.log(
        "📤 [ObjectDetectionHandler] Alert sent to proctor:",
        JSON.stringify(
          {
            type: alert.type,
            alertType: alert.alertType,
            details: alert.details,
          },
          null,
          2
        )
      );
    } catch (error) {
      console.error("❌ [ObjectDetectionHandler] Failed to send alert:", error);
    }
  }

  // Process image data using Rekognition
  async processImage(imageData) {
    try {
      const now = Date.now();
      const timeSinceLastDetection = now - this.lastObjectDetectionTime;

      // Always do face detection regardless of object detection cooldown
      const faceParams = {
        Image: { Bytes: imageData },
        Attributes: ["DEFAULT"],
      };

      const faceResult = await this.rekognition
        .detectFaces(faceParams)
        .promise();

      // Check for multiple faces
      if (faceResult.FaceDetails && faceResult.FaceDetails.length > 1) {
        const alertData = {
          message: "Multiple faces detected in exam",
          faceCount: faceResult.FaceDetails.length,
          count: 1,
        };

        console.log(
          `🚨 [ObjectDetectionHandler] Detected ${faceResult.FaceDetails.length} faces - Sending to proctor`
        );
        this.sendAlert("multiple_faces", alertData);

        // If in room scan mode, trigger the callback
        if (this.isRoomScanMode && typeof this.onDetection === "function") {
          this.onDetection({
            type: "multiple_faces",
            details: {
              message: "Multiple faces detected in exam",
              faceCount: faceResult.FaceDetails.length,
            },
          });
          return;
        }
      }

      // Skip object detection if in cooldown and not in room scan mode
      if (
        !this.isRoomScanMode &&
        timeSinceLastDetection < this.OBJECT_DETECTION_COOLDOWN
      ) {
        return;
      }

      // Proceed with object detection
      const objectParams = {
        Image: { Bytes: imageData },
        MaxLabels: 20,
        MinConfidence: this.isRoomScanMode ? 60 : 70,
      };

      const objectResult = await this.rekognition
        .detectLabels(objectParams)
        .promise();

      // Check for prohibited items - with deduplication by category
      const detectedItemsByCategory = new Map();

      for (const label of objectResult.Labels) {
        for (const [category, variations] of Object.entries(
          this.PROHIBITED_ITEMS
        )) {
          if (
            variations.some(
              (item) => label.Name.toLowerCase() === item.toLowerCase()
            )
          ) {
            // Only keep the highest confidence detection for each category
            const existingItem = detectedItemsByCategory.get(category);
            if (!existingItem || label.Confidence > existingItem.confidence) {
              detectedItemsByCategory.set(category, {
                category,
                name: label.Name,
                confidence: Math.round(label.Confidence),
              });
            }
            break;
          }
        }
      }

      const detectedItems = Array.from(detectedItemsByCategory.values());

      if (detectedItems.length > 0) {
        // Update cooldown timestamp when objects are detected
        this.lastObjectDetectionTime = now;

        // Prepare alert data
        const alertData = {
          message: `Prohibited items detected: ${detectedItems
            .map((item) => item.category)
            .join(", ")}`,
          detectedObjects: detectedItems.map((item) => item.category),
          detectedDetails: detectedItems,
          count: detectedItems.length,
        };

        const itemsList = detectedItems
          .map((item) => `${item.category} (${item.confidence}% confidence)`)
          .join(", ");
        console.log(
          `🚨 [ObjectDetectionHandler] Detected prohibited items: ${itemsList} - Sending to proctor`
        );

        // Send alert to proctor with required format
        this.sendAlert("prohibited_objects", alertData);

        // If in room scan mode, trigger the callback
        if (this.isRoomScanMode && typeof this.onDetection === "function") {
          this.onDetection({
            type: "prohibited_objects",
            details: {
              detectedObjects: detectedItems.map((item) => item.category),
              detectedDetails: detectedItems,
            },
          });
        }
      }
    } catch (error) {
      console.error("❌ [ObjectDetectionHandler] Rekognition error:", error);
    }
  }

  startRoomScan() {
    console.log("[ObjectDetectionHandler] Starting room scan mode");
    this.isRoomScanMode = true;
    this.lastObjectDetectionTime = 0; // Reset cooldowns for room scan
    this.lastFaceAlertTime = 0;
  }

  stopRoomScan() {
    console.log("[ObjectDetectionHandler] Stopping room scan mode");
    this.isRoomScanMode = false;
  }

  cleanup() {
    console.log("[ObjectDetectionHandler] Running cleanup");
    this.isRoomScanMode = false;
    this.lastObjectDetectionTime = 0;
    this.lastFaceAlertTime = 0;
    this.onDetection = null;
  }
}

export default ObjectDetectionHandler;
